'use client';

import { useRouter } from 'next/navigation';
import { useAppDispatch } from '@/app/lib/store/hooks';
import { clearUser } from '@/app/lib/store/user-slice';
import { UserClient } from '@/app/lib/schemas/types/app-types';
import ClientThemeProvider from '../../providers/client-theme-provider';
import { LogoutFailedError } from '@/app/lib/errors/auth-errors';
import { SubscriptionStatus } from '@prisma/client';
import { useMemo, useState } from 'react';
import { ExpandableMenuAccount } from './expandable-menu-account';

interface Props {
  user: UserClient;
}

export default function ExpandableMenuAccountContainer({ user }: Props) {
  const {
    personalData: { username, avatar },
    preferences: { showAvatar },
    status
  } = user;

  const [open, setOpen] = useState(false);

  const handleOpenChange = (newState: boolean) => {
    setOpen(newState);
  };

  const pending = useMemo(
    () => status === SubscriptionStatus.PENDING,
    [status]
  );
  const unsubscribed = useMemo(
    () => status === SubscriptionStatus.UNSUBSCRIBED,
    [status]
  );
  const subscribed = useMemo(
    () => status === SubscriptionStatus.SUBSCRIBED,
    [status]
  );

  const router = useRouter();

  const dispatch = useAppDispatch();

  const handleNavigateToPersonalAccount = () => {
    router.push('/myaccount/general');
    setTimeout(() => setOpen(false), 500);
  };

  const handleLogout = async () => {
    try {
      const response = await fetch('/api/auth/logout', {
        method: 'POST'
      });

      if (response.ok) {
        dispatch(clearUser());
        router.refresh();
      } else {
        const errorData = await response.json();
        console.error(errorData.error);
      }

      setOpen(false);
    } catch (error) {
      throw new LogoutFailedError(
        `Неудачная попытка выхода ${
          error instanceof Error ? error.message : ''
        }`
      );
    } finally {
      router.refresh();
    }
  };

  return (
    <ClientThemeProvider>
      <ExpandableMenuAccount
        open={open}
        username={username}
        showAvatar={showAvatar}
        avatar={avatar}
        pending={pending}
        unsubscribed={unsubscribed}
        subscribed={subscribed}
        onNavigateToPersonalAccount={handleNavigateToPersonalAccount}
        onLogout={handleLogout}
        onOpenChange={handleOpenChange}
      />
    </ClientThemeProvider>
  );
}
